import { axios_adapter } from '../network/axios_adapter';
import ENDPOINTS from '../network/endpoints';
import { COGNITO_REGION, COGNITO_USER_POOL_CLIENT_ID } from '../network/constants';
import { clearOtpChallengeSession, getOtpChallengeSession, setAuthSession, setOtpChallengeSession } from '../storage/authStorage';
import { setUserProfile } from '../storage/userProfileStorage';
import { identifyUser, trackEvent } from './clevertapService';

function getApiErrorMessage(error, fallback) {
  return (
    error?.response?.data?.__type ||
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    error?.Message ||
    error?.messageText ||
    error?.error_description ||
    error?.message ||
    fallback
  );
}

function toE164PhoneNumber(phone, countryCode) {
  return `+${String(countryCode || '').replace(/\D/g, '')}${String(phone || '').replace(/\D/g, '')}`;
}

function normalizeAuthMethod(method, email) {
  if (method === 'email' || email) return 'email';
  return 'phone';
}

function getAuthIdentifier({ phone, email, countryCode, method }) {
  const authMethod = normalizeAuthMethod(method, email);
  if (authMethod === 'email') return String(email || '').trim().toLowerCase();
  return toE164PhoneNumber(phone, countryCode);
}

async function cognitoRequest(target, body) {
  const response = await fetch(`https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': target,
    },
    body: JSON.stringify(body),
  });

  const payload = await response.json();

  if (!response.ok) {
    const error = new Error(payload?.message || 'Cognito request failed.');
    error.response = { data: payload };
    throw error;
  }

  return payload;
}

function getRequiredClientId() {
  if (!COGNITO_USER_POOL_CLIENT_ID) {
    throw new Error('Missing REACT_APP_COGNITO_USER_POOL_CLIENT_ID.');
  }
  return COGNITO_USER_POOL_CLIENT_ID;
}

async function sendOtpWithCognito({ phone, email, method, countryCode, countryIso }) {
  const clientId = getRequiredClientId();
  const authMethod = normalizeAuthMethod(method, email);
  const username = getAuthIdentifier({ phone, email, countryCode, method: authMethod });

  const payload = await cognitoRequest('AWSCognitoIdentityProviderService.InitiateAuth', {
    AuthFlow: 'CUSTOM_AUTH',
    ClientId: clientId,
    AuthParameters: {
      USERNAME: username,
    },
  });

  if (payload?.Session) {
    setOtpChallengeSession({
      session: payload.Session,
      challengeName: payload?.ChallengeName || 'CUSTOM_CHALLENGE',
    });
  }

  if (payload?.AuthenticationResult) {
    const result = payload.AuthenticationResult;
    setAuthSession({
      token: result.AccessToken || '',
      idToken: result.IdToken || '',
      refreshToken: result.RefreshToken || '',
      phone,
      email,
      method: authMethod,
      countryCode,
      countryIso,
      verified: true,
    });
  } else {
    setAuthSession({ phone, email, method: authMethod, countryCode, countryIso });
  }

  return {
    responseCode: 200,
    message: 'OTP sent successfully.',
    data: payload,
  };
}

async function verifyOtpWithCognito({ phone, email, method, countryCode, countryIso, otp }) {
  const clientId = getRequiredClientId();
  const authMethod = normalizeAuthMethod(method, email);
  const username = getAuthIdentifier({ phone, email, countryCode, method: authMethod });
  const challenge = getOtpChallengeSession();

  const payload = await cognitoRequest('AWSCognitoIdentityProviderService.RespondToAuthChallenge', {
    ChallengeName: challenge.challengeName || 'CUSTOM_CHALLENGE',
    ClientId: clientId,
    Session: challenge.session || undefined,
    ChallengeResponses: {
      USERNAME: username,
      ANSWER: otp,
      SMS_OTP_CODE: otp,
    },
  });

  const authResult = payload?.AuthenticationResult;

  if (!authResult?.AccessToken && !authResult?.IdToken) {
    throw new Error('OTP verification did not complete. Please request a new OTP.');
  }

  setAuthSession({
    token: authResult.AccessToken || '',
    idToken: authResult.IdToken || '',
    refreshToken: authResult.RefreshToken || '',
    phone,
    email,
    method: authMethod,
    countryCode,
    countryIso,
    verified: true,
  });
  clearOtpChallengeSession();
  trackEvent('OTP Verified', {
    Method: authMethod,
  });

  return {
    responseCode: 200,
    message: 'OTP verified successfully.',
    data: payload,
  };
}

export async function sendOtp({ phone, email, method, countryCode, countryIso }) {
  const authMethod = normalizeAuthMethod(method, email);
  return sendOtpWithCognito({ phone, email, method: authMethod, countryCode, countryIso });
}

export async function resendOtp({ phone, email, method, countryCode, countryIso }) {
  const authMethod = normalizeAuthMethod(method, email);
  return sendOtpWithCognito({ phone, email, method: authMethod, countryCode, countryIso });
}

export async function verifyOtp({ phone, email, method, countryCode, countryIso, otp }) {
  const authMethod = normalizeAuthMethod(method, email);
  return verifyOtpWithCognito({ phone, email, method: authMethod, countryCode, countryIso, otp });
}

export async function fetchUserProfile() {
  const response = await axios_adapter.get(ENDPOINTS.GET_PROFILE);
  const body = response.data;

  if (body?.responseCode && body.responseCode !== 200) {
    throw new Error(body?.message || 'Unable to fetch profile.');
  }

  const profile = body?.data ?? body;
  //profile.totalXP = 10000000000000000000000000;
  setUserProfile(profile);
  identifyUser(profile);
  trackEvent('Profile Loaded');
  return profile;
}

export async function validateAuthToken() {
  await fetchUserProfile();
}

const COGNITO_UNREGISTERED_ERROR_TYPES = new Set([
  'UserNotFoundException',
  'ResourceNotFoundException',
]);

function getCognitoErrorType(error) {
  return error?.response?.data?.__type || error?.name || error?.code || '';
}

export function isCognitoRegistrationRequiredError(error) {
  const errorType = getCognitoErrorType(error);
  if (COGNITO_UNREGISTERED_ERROR_TYPES.has(errorType)) {
    return true;
  }

  const message = getApiErrorMessage(error, '').toLowerCase();
  return (
    message.includes('user does not exist') ||
    message.includes('user not found') ||
    message.includes('userlambdavalidationexception') ||
    message.includes('usernotfoundexception')
  );
}

export { getApiErrorMessage };
